import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useState } from 'react';
import { useAsync } from 'react-async';
import {
  KoobButton,
  KoobInputSegmented,
  KoobInputStepperField,
} from '@koob/ui';
import { useError, useSnack } from '../../hooks';
import {
  getOrganization,
  updateOrganization,
} from '../../api/node/organization';

// Stored in hours; shown in days from 24 hours up.
const toDuration = (hours) =>
  hours >= 24
    ? { amount: Math.ceil(hours / 24), unit: 'day' }
    : { amount: hours, unit: 'hour' };

// How long before check-in the DMC's clients (TOs) may still book: one row of
// the distribution card, edited in place.
const TosMaximumTimeBeforeBooking = ({ isAllowedToManage, organizationId }) => {
  const { t } = useTranslation('organization');
  const { sendErrorSnack } = useError();
  const { notify } = useSnack();
  const [editing, setEditing] = useState(false);
  const { data, reload } = useAsync({
    promiseFn: getOrganization,
    id: organizationId,
    filters: { withDetails: false },
    watch: organizationId,
  });
  const organization = data?.data;
  const hours = organization?.maxReservationInterval;
  const isSet = hours !== undefined && hours !== null && hours >= 0;
  const current = isSet ? toDuration(hours) : { amount: 1, unit: 'day' };

  const save = async ({ amount, unit }) => {
    try {
      await updateOrganization(organizationId, {
        maxReservationInterval: unit === 'day' ? amount * 24 : amount,
        canBeRequestedForToAccess: organization?.canBeRequestedForToAccess,
      });
      notify(t('organizationUpdateSuccess'));
      setEditing(false);
      reload();
    } catch (err) {
      sendErrorSnack(err);
    }
  };

  if (editing) {
    return (
      <Formik
        initialValues={current}
        validationSchema={Yup.object().shape({
          amount: Yup.number().required(t('errors:required')),
        })}
        onSubmit={save}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form className="flex flex-wrap items-center gap-3">
            <KoobInputStepperField name="amount" min={1} />
            <KoobInputSegmented
              options={[
                { label: t('misc:duration.day'), value: 'day' },
                { label: t('misc:duration.hour'), value: 'hour' },
              ]}
              value={values.unit}
              onChange={(value) => setFieldValue('unit', value)}
            />
            <span className="text-sm text-k-p">
              {t('distribution.beforeCheckIn')}
            </span>
            <div className="flex gap-2">
              <KoobButton
                variant="secondary"
                type="button"
                onClick={() => setEditing(false)}
              >
                {t('misc:cancel')}
              </KoobButton>
              <KoobButton
                variant="primary"
                type="submit"
                loading={isSubmitting}
              >
                {t('misc:save')}
              </KoobButton>
            </div>
          </Form>
        )}
      </Formik>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-sm text-k-h">
        {isSet
          ? t(`distribution.cutoff.${current.unit}`, { count: current.amount })
          : t('distribution.cutoffNotSet')}
      </span>
      {isAllowedToManage && organization && (
        <KoobButton variant="secondary" onClick={() => setEditing(true)}>
          {t('misc:edit')}
        </KoobButton>
      )}
    </div>
  );
};

export default TosMaximumTimeBeforeBooking;
